import { createContext, useEffect, useState } from 'react';
import { configApi } from '../services/configApi';
import { ICandidateData } from './types';
import { Spinner, Stack, Text, useMediaQuery, useToast } from '@chakra-ui/react';
import { decodeToken } from '../utils/functions';
import { useGetClientData } from '../services/hooks/ClientData/useGetClientData';

interface IAuth {
  signIn(email: string, password: string): Promise<void>;
  signOut(): void;
  setCandidate: React.Dispatch<React.SetStateAction<ICandidateData | undefined>>;
  isAuth?: boolean;
  isMobile: boolean;
  candidate?: ICandidateData;
  loadingCandidate: boolean;
  refetchCandidate(): void;
}

export const ContextAuth = createContext({} as IAuth);

interface IOwnProps {
  children: React.ReactNode;
}

const ProviderAuth: React.FC<IOwnProps> = ({ children }) => {
  const ClientData = useGetClientData();
  const [isAuth, setIsAuth] = useState<boolean>();
  const [candidate, setCandidate] = useState<ICandidateData>();
  const [loadingCandidate, setLoadingCandidate] = useState<boolean>(false);
  const [isMobile] = useMediaQuery('(max-width: 1024px)');
  const toast = useToast();

  const autoSignOut = () => {
    const date = new Date();

    const currentDateTime = new Date(date.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' })).getTime();
    const currentDateTimeSeconds = Math.floor(currentDateTime / 1000);

    const decodeTk = decodeToken();

    if (decodeTk) {
      const last_time = decodeTk.exp;

      if (currentDateTimeSeconds < last_time) {
        const time_left = last_time - currentDateTimeSeconds;

        setTimeout(() => {
          signOut();
        }, time_left * 1000);
      }
    }
  };

  const getDataUser = () => {
    setLoadingCandidate(true);
    configApi
      .get('candidate/my-data/')
      .then((candidateData) => {
        setCandidate(candidateData.data);
        setIsAuth(true);
      })
      .catch(() => {
        signOut();
      })
      .finally(() => {
        setLoadingCandidate(false);
      });
  };

  useEffect(() => {
    let canUseLocalStorage = false;

    try {
      canUseLocalStorage = !!window.localStorage;
    } catch (e) {
      toast({
        title: 'Atenção',
        description: 'Permissões invalidas',
        status: 'warning',
        duration: 7000,
        isClosable: true,
      });
    }

    if (canUseLocalStorage) {
      const token = localStorage.getItem('token');

      if (token) {
        localStorage.setItem('token', token);
        configApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        autoSignOut();
        getDataUser();
      } else {
        setIsAuth(false);
      }
    }
  }, []);

  const signIn = async (email: string, password: string) => {
    await configApi
      .post('auth/login/', {
        email: email.toLowerCase().trim(),
        password: password,
      })
      .then((response) => {
        let canUseLocalStorage = false;

        try {
          canUseLocalStorage = !!window.localStorage;
        } catch (e) {
          toast({
            title: 'Atenção',
            description: 'Permissões invalidas',
            status: 'warning',
            duration: 7000,
            isClosable: true,
          });
        }

        if (canUseLocalStorage && response?.data) {
          localStorage.setItem('token', response?.data?.access);
          configApi.defaults.headers.common['Authorization'] = `Bearer ${localStorage?.getItem('token')}`;
          autoSignOut();
          getDataUser();
        }
      })
      .catch((error) => {
        throw error;
      });
  };

  const signOut = () => {
    let canUseLocalStorage = false;

    try {
      canUseLocalStorage = !!window.localStorage;
    } catch (e) {
      toast({
        title: 'Atenção',
        description: 'Permissões invalidas',
        status: 'warning',
        duration: 7000,
        isClosable: true,
      });
    }

    if (canUseLocalStorage) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      configApi.defaults.headers.common['Authorization'] = '';
      setIsAuth(false);
      toast({
        title: '',
        description: 'Sessão encerrada',
        status: 'info',
        duration: 4000,
        isClosable: true,
      });
      setCandidate(undefined);
    }
  };

  if (ClientData?.isLoading) {
    return (
      <Stack w="100%" h="100dvh" align="center" justify="center">
        <Spinner size="xl" />
        <Text>Carregando dados</Text>
      </Stack>
    );
  }

  return (
    <ContextAuth.Provider
      value={{
        isAuth,
        isMobile,
        signIn,
        signOut,
        setCandidate,
        loadingCandidate,
        candidate,
        refetchCandidate: getDataUser,
      }}
    >
      {children}
    </ContextAuth.Provider>
  );
};

export default ProviderAuth;
